import { Component, OnDestroy, OnInit, ChangeDetectorRef, inject, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { DrawerComponent } from 'src/app/components/layout/kt/components/_DrawerComponent';

import { PwaModel } from 'src/app/models/pwa.model';
import { PwaService } from 'src/app/services/pwa.service';
import { GlobalStoreService } from 'src/app/services/global-store.service';
import { CompaniesService } from 'src/app/services/company.service';
import { ToastService } from 'src/app/services/toast.service';
import { SharedImports } from 'src/app/shared-imports';

import { MatExpansionModule } from '@angular/material/expansion';
import { PwaTheme } from 'src/app/models/pwa.model.theme';

import { ColorPickerDirective } from 'ngx-color-picker';
import { PwaThemePropertyModel } from 'src/app/models/pwa.theme.property.model';
import { PwaThemePropertyTypeEnum } from 'src/app/enums/pwa-theme-property-type-enum';

@Component({
	selector: 'app-create-pwa-drawer',
	templateUrl: './create-pwa-drawer.component.html',
	standalone: true,
	imports: [SharedImports, MatExpansionModule, ColorPickerDirective],
})
export class CreatePwaDrawerComponent implements OnInit, OnDestroy, AfterViewInit {
	form!: FormGroup;
	pwa: PwaModel | null = null;
	activeCompany: any;
	pwaImage: any;

	pwa$: BehaviorSubject<PwaModel | null> = new BehaviorSubject<PwaModel | null>(null);

	private mutationObserver: MutationObserver | null = null;
	private _subscriptions: Subscription[] = [];

	private fb = inject(FormBuilder);
	private pwaService = inject(PwaService);
	private globalStoreService = inject(GlobalStoreService);
	private companiesService = inject(CompaniesService);
	private toastService = inject(ToastService);
	private cdr = inject(ChangeDetectorRef);
	public defaultThemeArray: PwaTheme = new PwaTheme();

	private readonly referenceTheme: PwaTheme = new PwaTheme();

	theme: string = '';
	PwaThemePropertyTypeEnum = PwaThemePropertyTypeEnum;
	pwaTheme: any;
	pwaCover: any;
	selectedLogotype: any;
	selectedCover: any;

	constructor() {
		/* Active company */
		this._subscriptions.push(
			this.companiesService.company$.subscribe((company) => {
				if (company?.uuid) {
					this.activeCompany = company;
				}
			})
		);
	}

	ngOnInit(): void {
		this.initForm();

		/* Edit PWA */
		this._subscriptions.push(
			this.globalStoreService.editingPwa$.subscribe((pwa) => {
				this.pwa = pwa;
				this.pwa$.next(pwa);
				if (pwa) {
					this.form.patchValue({
						name: pwa.name ?? '',
						luopeId: pwa.luopeId ?? '',
						sharedKey: pwa.sharedKey ?? '',
						subdomain: pwa.subdomain ?? '',
						active: pwa.active ?? false,
						finalMessage: pwa.finalMessage ?? '',
						activeFinalMessage: pwa.activeFinalMessage ?? false,
					});
					this.initTheme(pwa.theme);
					this.globalStoreService.logoFileToTerminal$.next(pwa.logo);
					this.globalStoreService.coverFileToTerminal$.next(pwa.cover);
				} else {
					this.resetForm();
				}
				this.cdr.detectChanges();
			})
		);

		this._subscriptions.push(
			this.globalStoreService.logoFileToTerminal$.subscribe((file) => {
				if (file?.path) {
					this.selectedLogotype = file;
					this.pwaImage = file;
				} else {
					this.selectedLogotype = null;
				}
			})
		);

		this._subscriptions.push(
			this.globalStoreService.coverFileToTerminal$.subscribe((file) => {
				if (file?.path) {
					this.selectedCover = file;
					this.pwaCover = file;
				} else {
					this.selectedCover = null;
				}
			})
		);
	}

	isDefaultValue(property: PwaThemePropertyModel): boolean {
		const referenceProperty = this.referenceTheme.properties.find((p) => p.name === property.name);
		return referenceProperty ? referenceProperty.value === property.value : true;
	}

	resetToDefault(property: PwaThemePropertyModel) {
		const referenceProperty = this.referenceTheme.properties.find((p) => p.name === property.name);
		if (referenceProperty) {
			property.value = referenceProperty.value;
		}
	}

	get isDefaultTheme(): boolean {
		return this.defaultThemeArray.properties.every((property) => {
			const referenceProperty = this.referenceTheme.properties.find((p) => p.name === property.name);
			return referenceProperty ? referenceProperty.value === property.value : true;
		});
	}

	resetTheme() {
		this.defaultThemeArray = new PwaTheme();
	}

	updateThemeProperty($event: string, property: PwaThemePropertyModel) {
		property.value = $event;
	}

	ngAfterViewInit(): void {
		this.mutationObserver = new MutationObserver(() => {
			const drawer = DrawerComponent.getInstance('kt_create_pwa');
			if (drawer) {
				drawer.on('kt.drawer.hide', () => {
					this.stopObservables();
				});
			}
		});

		this.mutationObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}

	private initTheme(theme: any) {
		this.defaultThemeArray = new PwaTheme();

		Object.keys(theme).forEach((key) => {
			const existingProperty = this.defaultThemeArray.properties.find((p) => p.name === key);
			if (existingProperty) {
				existingProperty.value = theme[key];
			}
		});
	}

	prepareTheme(): any {
		const themeObject: any = {};

		this.defaultThemeArray.properties.forEach((property) => {
			themeObject[property.name] = property.value;
		});

		return themeObject;
	}

	private initForm(): void {
		this.form = this.fb.group({
			name: ['', [Validators.required, Validators.minLength(2)]],
			luopeId: ['', Validators.required],
			sharedKey: ['', Validators.required],
			subdomain: ['', Validators.required],
			activeFinalMessage: [false],
			active: [false],
			theme: [{}, Validators.required],
			finalMessage: [''],
		});
	}

	save(): void {
		if (this.form.invalid || !this.activeCompany) {
			this.form.markAllAsTouched();
			return;
		}

		this.form.get('theme')?.setValue(this.prepareTheme());

		const pwa: PwaModel = {
			...this.form.value,
		};

		if (!this.isFinalMessageValid) return;

		if (this.pwa?.uuid) {
			this.updatePwa(pwa);
		} else {
			this.createPwa(pwa);
		}
	}

	private createPwa(pwa: PwaModel): void {
		pwa.active = false;
		if (this.pwaImage?.uuid) {
			pwa.logo = this.pwaImage;
		}
		if (this.pwaCover?.uuid) {
			pwa.cover = this.pwaCover;
		}
		this.pwaService.postOneCompanyPwa(this.activeCompany.uuid, pwa).subscribe({
			next: () => {
				this.toastService.success('toast_pwa_created', 'Success');
				this.closeDrawer();
			},
			error: () => {
				this.toastService.error('toast_error_creating_pwa', 'Error');
			},
		});
	}

	private updatePwa(payload: Partial<PwaModel>): void {
		if (!this.pwa?.uuid) return;
		if (this.pwaImage?.uuid) {
			payload.logo = this.pwaImage;
		}
		if (this.pwaCover?.uuid) {
			payload.cover = this.pwaCover;
		}
		this.pwaService.patchOneCompanyPwa(this.activeCompany.uuid, this.pwa.uuid, payload).subscribe({
			next: () => {
				this.toastService.success('toast_pwa_updated', 'Success');
				this.closeDrawer();
			},
			error: () => {
				this.toastService.error('toast_error_updating_pwa', 'Error');
			},
		});
	}

	openFilesGalery() {
		// Reset all flags first
		this.globalStoreService.editingLogoTerminal$.next(true);

		const drawerId = 'kt_select_files';
		const drawer = DrawerComponent.getInstance(drawerId);

		if (drawer) {
			drawer.show();
		}
	}

	openCoverGallery() {
		// Reset all flags first
		this.globalStoreService.editingCoverTerminal$.next(true);

		const drawerId = 'kt_select_files';
		const drawer = DrawerComponent.getInstance(drawerId);

		if (drawer) {
			drawer.show();
		}
	}

	get isFinalMessageValid(): boolean {
		if (this.form.get('activeFinalMessage')?.value) {
			return this.form.get('finalMessage')?.value.trim().length > 0;
		}
		return true;
	}

	removeTerminalLogo(event: Event) {
		this.globalStoreService.logoFileToTerminal$.next({ file: null });
		event.stopPropagation();
	}

	removeCoverLogo(event: Event) {
		this.globalStoreService.coverFileToTerminal$.next({ file: null });
		event.stopPropagation();
	}

	resetForm() {
		this.form.reset({
			name: '',
			luopeId: '',
			sharedKey: '',
			subdomain: '',
			activeFinalMessage: false,
			active: false,
			theme: {},
			finalMessage: '',
		});

		this.defaultThemeArray = new PwaTheme();
	}

	stopObservables() {
		this.resetForm();

		this.pwa = null;
		this.globalStoreService.editingPwa$.next(null);
		this.globalStoreService.logoFileToTerminal$.next({ file: null });
		this.globalStoreService.coverFileToTerminal$.next({ file: null });

		this.pwa$.next(null);
	}

	closeDrawer(): void {
		this.stopObservables();

		const drawer = DrawerComponent.getInstance('kt_create_pwa');
		if (drawer && drawer.isShown()) {
			drawer.hide();
		}
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach((s) => s.unsubscribe());
		this._subscriptions = [];
		this.initForm();
	}
}
